import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";
import { serviceNames } from "../utils/constants";
import { ImageTypes, PerPhotoType } from "../utils/enums";
import { formatToBase64ImageUrl } from "../utils/functions";
import { createAuditLog } from "../utils/commonServices";
import { getLoginUsername } from "./PermissionsReducer";
import { setGlobalCircularLoading } from "./UiReducer";

const initialState = {
	inspections: [],
	loading: false,
	error: null,
	total: 0,
};

const FETCH_INSPECTIONS_REQUEST = "FETCH_INSPECTIONS_REQUEST";
const FETCH_INSPECTIONS_SUCCESS = "FETCH_INSPECTIONS_SUCCESS";
const FETCH_INSPECTIONS_FAILURE = "FETCH_INSPECTIONS_FAILURE";
const CREATE_INSPECTION_REQUEST = "CREATE_INSPECTION_REQUEST";
const CREATE_INSPECTION_SUCCESS = "CREATE_INSPECTION_SUCCESS";
const CREATE_INSPECTION_FAILURE = "CREATE_INSPECTION_FAILURE";
const UPDATE_INSPECTION_REQUEST = "UPDATE_INSPECTION_REQUEST";
const UPDATE_INSPECTION_SUCCESS = "UPDATE_INSPECTION_SUCCESS";
const UPDATE_INSPECTION_FAILURE = "UPDATE_INSPECTION_FAILURE";
const DELETE_INSPECTION_REQUEST = "DELETE_INSPECTION_REQUEST";
const DELETE_INSPECTION_SUCCESS = "DELETE_INSPECTION_SUCCESS";
const DELETE_INSPECTION_FAILURE = "DELETE_INSPECTION_FAILURE";

// Fields to track for audit logging
const AUDIT_FIELDS = [
	"vehicleId",
	"type",
	"date",
	"odometer",
	"overallStatus",
	"perPersonnelId",
	"notes",
	"resolvedAt",
	"resolvedBy",
	"resolvedComments",
];

// Single Audit Log Helper for Inspection
const logInspectionAudit = async ({
	action,
	recordId,
	oldData = null,
	newData = null,
	changedBy,
	agencyId,
}) => {
	try {
		let oldValues = null;
		let newValues = null;

		if (action === "CREATE") {
			// For CREATE, log all new values
			newValues = {};
			AUDIT_FIELDS.forEach((field) => {
				if (
					newData?.[field] !== undefined &&
					newData?.[field] !== null
				) {
					newValues[field] = newData[field];
				}
			});
		} else if (action === "DELETE") {
			// For DELETE, log key old values
			oldValues = {};
			AUDIT_FIELDS.forEach((field) => {
				if (
					oldData?.[field] !== undefined &&
					oldData?.[field] !== null
				) {
					oldValues[field] = oldData[field];
				}
			});
		} else if (action === "UPDATE") {
			// For UPDATE, log only changed fields
			oldValues = {};
			newValues = {};
			AUDIT_FIELDS.forEach((field) => {
				const oldVal = oldData?.[field];
				const newVal = newData?.[field];
				if (oldVal !== newVal) {
					oldValues[field] = oldVal ?? null;
					newValues[field] = newVal ?? null;
				}
			});

			// Don't log if no changes
			if (
				Object.keys(oldValues).length === 0 &&
				Object.keys(newValues).length === 0
			) {
				return;
			}
		}

		await createAuditLog({
			tableName: "Inspection",
			recordId,
			action,
			changedBy: changedBy || "Unknown",
			changedAt: new Date().toISOString(),
			oldValues: oldValues ? JSON.stringify(oldValues) : null,
			newValues: newValues ? JSON.stringify(newValues) : null,
			agencyId,
		});
	} catch (error) {
		console.error("Failed to create audit log:", error);
		// Don't throw - audit failure shouldn't break main operation
	}
};

// Helper to save inspection photos using personnel-photos service
const saveInspectionPhotos = async (
	inspectionId,
	photos,
	userAgency,
	userId,
) => {
	try {
		const service = getService(serviceNames.personnelPhotos);

		const newPhotos = photos || [];

		// Create new ones
		for (const photo of newPhotos) {
			let imageData = photo;

			await service.create({
				entityId: inspectionId,
				photoFor: PerPhotoType.Inspection,
				imageType: ImageTypes.Binary,
				imageData: imageData,
				fileName: photo.name || `photo-${Date.now()}.jpg`,
				notes: photo.notes || null,
				isThumbnail: false,
				isDeleted: false,
				createdBy: userId,
			});
		}

		return true;
	} catch (error) {
		console.error("Error saving inspection photos:", error);
		throw error;
	}
};

// Helper to fetch inspection photos
export const fetchInspectionPhotos = async (inspectionId) => {
	try {
		const service = getService(serviceNames.personnelPhotos);
		const response = await service.find({
			query: {
				entityId: inspectionId,
				photoFor: PerPhotoType.Inspection,
				isDeleted: false,
			},
		});

		const photos = response || [];

		return photos.map((photo) => {
			let imageSrc = null;

			if (photo.imageData) {
				try {
					imageSrc = formatToBase64ImageUrl(photo.imageData);
				} catch (err) {
					console.error("Error converting image data:", err);
				}
			}

			return {
				id: photo.imageID,
				src: imageSrc,
				name: photo.fileName,
				size: 0,
				notes: photo.notes,
			};
		});
	} catch (error) {
		console.error("Error fetching inspection photos:", error);
		return [];
	}
};

// Helper to save inspection responses (same pattern as saveVehicleServiceRequirements)
const saveInspectionResponses = async (inspectionId, responses, userAgency) => {
	try {
		const service = getService(serviceNames.perInspectionResponses);

		// Get existing responses
		const existingResponses = await service.find({
			query: {
				inspectionId: inspectionId,
				isDeleted: false,
				$limit: -1,
			},
		});

		const existingData = existingResponses?.data || existingResponses || [];
		const existingItemIds = existingData.map(
			(resp) => resp.checklistItemId,
		);
		const newResponses = responses || [];
		const newItemIds = newResponses.map((r) => r.checklistItemId);

		// Find responses to delete
		const toDelete = existingData.filter(
			(resp) => !newItemIds.includes(resp.checklistItemId),
		);

		// Find responses to create
		const toCreate = newResponses.filter(
			(resp) => !existingItemIds.includes(resp.checklistItemId),
		);

		// Find responses to update
		const toUpdate = newResponses.filter((resp) =>
			existingItemIds.includes(resp.checklistItemId),
		);

		// Delete removed ones
		for (const resp of toDelete) {
			await service.patch(resp.id, { isDeleted: true });
		}

		// Create new ones
		for (const response of toCreate) {
			await service.create({
				inspectionId: inspectionId,
				checklistItemId: response.checklistItemId,
				status: response.status,
				comment: response.comment || null,
				agencyId: userAgency,
				isDeleted: false,
			});
		}

		// Update existing ones
		for (const response of toUpdate) {
			const existingResp = existingData.find(
				(r) => r.checklistItemId === response.checklistItemId,
			);
			if (existingResp) {
				await service.patch(existingResp.id, {
					status: response.status,
					comment: response.comment || null,
				});
			}
		}

		return true;
	} catch (error) {
		console.error("Error saving inspection responses:", error);
		throw error;
	}
};

export const getInspectionsList =
	(
		page = 0,
		pageSize = 20,
		sortField = "id",
		sortDirection = "desc",
		search = "",
		filters = {},
		userAgency,
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		const query = {
			$limit: pageSize,
			$skip: page * pageSize,
			$sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
			isDeleted: false,
			agencyId: userAgency,
		};

		// Text search
		if (search) {
			query.$or = [
				{ id: { $like: `%${search}%` } },
				{ type: { $like: `%${search}%` } },
			];
		}

		// Helper function
		const getQueryValue = (input) => {
			if (Array.isArray(input)) {
				const values = input.map((i) => i.value).filter(Boolean);
				return values.length > 0 ? { $in: values } : undefined;
			} else if (input?.value) {
				return input.value;
			}
			return undefined;
		};

		// Multi-select filter fields
		const multiSelectFields = [
			"vehicleId",
			"type",
			"perPersonnelId",
			"overallStatus",
		];

		multiSelectFields.forEach((field) => {
			const value = getQueryValue(filters[field]);
			if (value !== undefined) {
				query[field] = value;
			}
		});

		// Status filter
		if (filters.status?.value && filters.status.value !== "all") {
			query.overallStatus = filters.status.value;
		}

		// Date range filters
		if (filters.from) {
			query.date = { ...(query.date || {}), $gte: filters.from };
		}
		if (filters.to) {
			query.date = { ...(query.date || {}), $lte: filters.to };
		}

		// Period filter
		if (filters.period?.value) {
			const now = new Date();
			let startDate;

			switch (filters.period.value) {
				case "today":
					startDate = new Date(now.setHours(0, 0, 0, 0));
					break;
				case "week":
					startDate = new Date(now.setDate(now.getDate() - 7));
					break;
				case "month":
					startDate = new Date(now.setMonth(now.getMonth() - 1));
					break;
				case "quarter":
					startDate = new Date(now.setMonth(now.getMonth() - 3));
					break;
				case "year":
					startDate = new Date(
						now.setFullYear(now.getFullYear() - 1),
					);
					break;
				default:
					break;
			}

			if (startDate) {
				query.date = {
					...(query.date || {}),
					$gte: startDate.toISOString(),
				};
			}
		}

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: FETCH_INSPECTIONS_REQUEST,
				success: FETCH_INSPECTIONS_SUCCESS,
				failure: FETCH_INSPECTIONS_FAILURE,
			},
			apiCall: async () => {
				dispatch(setGlobalCircularLoading(true));
				const response = await service.find({ query });
				const inspectionsData = response?.data || response || [];

				// Fetch responses for each inspection
				const responsesService = getService(
					serviceNames.perInspectionResponses,
				);

				const inspectionsWithDetails = await Promise.all(
					inspectionsData.map(async (inspection) => {
						try {
							// Get responses
							const responsesResponse =
								await responsesService.find({
									query: {
										inspectionId: inspection.id,
										isDeleted: false,
										$limit: -1,
									},
								});

							const responses =
								responsesResponse?.data ||
								responsesResponse ||
								[];

							// Get photos using personnel-photos service
							const photos = await fetchInspectionPhotos(
								inspection.id,
							);

							return {
								...inspection,
								responses: responses.map((r) => ({
									checklistItemId: r.checklistItemId,
									status: r.status,
									comment: r.comment,
								})),
								photos: photos,
							};
						} catch (error) {
							console.error(
								`Error fetching inspection details for ${inspection.id}:`,
								error,
							);
							return {
								...inspection,
								responses: [],
								photos: [],
							};
						}
					}),
				);

				dispatch(setGlobalCircularLoading(false));
				return {
					inspections: inspectionsWithDetails,
					total: response.total || inspectionsWithDetails.length,
				};
			},
			transformResult: (res) => ({
				inspections: res?.inspections,
				total: res?.total,
			}),
			errorMessagePrefix: "Failed to fetch inspections",
		});
	};

export const createInspection =
	(inspectionData, responses = [], photos = [], userAgency, userId) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: CREATE_INSPECTION_REQUEST,
				success: CREATE_INSPECTION_SUCCESS,
				failure: CREATE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				const inspection = await service.create({
					...inspectionData,
					agencyId: userAgency,
					createdBy: userId,
					isDeleted: false,
				});

				// Save responses
				if (responses.length > 0 && inspection.id) {
					await saveInspectionResponses(
						inspection.id,
						responses,
						userAgency,
					);
				}

				// Save photos using personnel-photos service
				if (photos.length > 0 && inspection.id) {
					await saveInspectionPhotos(
						inspection.id,
						photos,
						userAgency,
						userId,
					);
				}

				// Update vehicle odometer
				if (inspectionData.odometer && inspectionData.vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(inspectionData.vehicleId, {
						odometer: inspectionData.odometer,
						lastMileage: inspectionData.odometer,
					});
				}

				// Create Audit Log Entry
				await logInspectionAudit({
					action: "CREATE",
					recordId: inspection.id,
					newData: {
						...inspectionData,
						agencyId: userAgency,
						createdBy: userId,
						isDeleted: false,
					},
					changedBy: getLoginUsername() || "",
					agencyId: userAgency,
				});

				return {
					...inspection,
					responses,
					photos: photos || [],
				};
			},
			successMessage: "Inspection created successfully",
			errorMessagePrefix: "Failed to create inspection",
		});
	};

export const updateInspection =
	(
		id,
		inspectionData,
		responses = [],
		photos = [],
		userAgency,
		userId,
		oldData = {},
	) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_INSPECTION_REQUEST,
				success: UPDATE_INSPECTION_SUCCESS,
				failure: UPDATE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				const inspection = await service.patch(id, {
					...inspectionData,
					updatedBy: userId,
				});

				// Update responses
				await saveInspectionResponses(id, responses, userAgency);

				// Update photos using personnel-photos service
				await saveInspectionPhotos(id, photos, userAgency, userId);

				// Update vehicle odometer
				if (inspectionData.odometer && inspectionData.vehicleId) {
					const vehicleService = getService(serviceNames.perVehicles);
					await vehicleService.patch(inspectionData.vehicleId, {
						odometer: inspectionData.odometer,
						lastMileage: inspectionData.odometer,
					});
				}

				// Fetch updated photos
				const updatedPhotos = await fetchInspectionPhotos(id);

				// Create Audit Log Entry
				await logInspectionAudit({
					action: "UPDATE",
					recordId: id,
					oldData,
					newData: inspectionData,
					changedBy: getLoginUsername() || "",
					agencyId: userAgency,
				});

				return {
					...inspection,
					responses,
					photos: updatedPhotos,
				};
			},
			successMessage: "Inspection updated successfully",
			errorMessagePrefix: "Failed to update inspection",
		});
	};

export const resolveInspection =
	(id, resolveData, userId, userAgency, oldData = {}) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: UPDATE_INSPECTION_REQUEST,
				success: UPDATE_INSPECTION_SUCCESS,
				failure: UPDATE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				const inspection = await service.patch(id, {
					resolvedAt: resolveData.resolvedAt,
					resolvedBy: userId,
					resolvedComments: resolveData.resolvedComments,
					updatedBy: userId,
				});
				// Create Audit Log Entry
				await logInspectionAudit({
					action: "UPDATE",
					recordId: id,
					oldData,
					newData: {
						resolvedAt: resolveData.resolvedAt,
						resolvedBy: userId,
						resolvedComments: resolveData.resolvedComments,
					},
					changedBy: getLoginUsername() || "",
					agencyId: userAgency,
				});
				return inspection;
			},
			successMessage: "Inspection resolved successfully",
			errorMessagePrefix: "Failed to resolve inspection",
		});
	};

export const deleteInspection =
	(id, userId, userAgency, oldData = {}) =>
	async (dispatch) => {
		const service = getService(serviceNames.perInspections);
		const patchData = {
			isDeleted: true,
			deletedBy: userId,
			deletedDateTime: new Date().toISOString(),
		};

		return await asyncThunkWrapper({
			dispatch,
			types: {
				request: DELETE_INSPECTION_REQUEST,
				success: DELETE_INSPECTION_SUCCESS,
				failure: DELETE_INSPECTION_FAILURE,
			},
			apiCall: async () => {
				await service.patch(id, patchData);

				// Soft delete responses
				const responsesService = getService(
					serviceNames.perInspectionResponses,
				);
				const responses = await responsesService.find({
					query: { inspectionId: id, isDeleted: false, $limit: -1 },
				});

				const existingResponses = responses?.data || responses || [];
				for (const resp of existingResponses) {
					await responsesService.patch(resp.id, { isDeleted: true });
				}

				// Soft delete photos using personnel-photos service
				const photosService = getService(serviceNames.personnelPhotos);
				const photos = await photosService.find({
					query: {
						entityId: id,
						photoFor: PerPhotoType.Inspection,
						isDeleted: false,
					},
				});

				const existingPhotos = photos || [];
				for (const photo of existingPhotos) {
					await photosService.patch(photo.imageID, {
						isDeleted: true,
						deletedBy: userId,
						deletedDateTime: new Date().toISOString(),
					});
				}

				// Create Audit Log Entry
				await logInspectionAudit({
					action: "DELETE",
					recordId: id,
					oldData,
					changedBy: getLoginUsername() || "",
					agencyId: userAgency,
				});

				return id;
			},
			transformResult: () => id,
			successMessage: "Inspection deleted successfully",
			errorMessagePrefix: "Failed to delete inspection",
		});
	};

export default function inspectionsReducer(state = initialState, action) {
	switch (action.type) {
		case FETCH_INSPECTIONS_REQUEST:
		case CREATE_INSPECTION_REQUEST:
		case UPDATE_INSPECTION_REQUEST:
		case DELETE_INSPECTION_REQUEST:
			return { ...state, loading: true, error: null };
		case FETCH_INSPECTIONS_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: action.payload.inspections,
				total: action.payload.total,
			};
		case CREATE_INSPECTION_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: [action.payload, ...state.inspections],
				total: state.total + 1,
			};
		case UPDATE_INSPECTION_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: state.inspections.map((i) =>
					i.id === action.payload.id ? action.payload : i,
				),
			};
		case DELETE_INSPECTION_SUCCESS:
			return {
				...state,
				loading: false,
				inspections: state.inspections.filter(
					(i) => i.id !== action.payload,
				),
				total: state.total - 1,
			};
		case FETCH_INSPECTIONS_FAILURE:
		case CREATE_INSPECTION_FAILURE:
		case UPDATE_INSPECTION_FAILURE:
		case DELETE_INSPECTION_FAILURE:
			return { ...state, loading: false, error: action.payload };
		default:
			return state;
	}
}
